import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";

import { regexForName } from "common/constant";

import { getConsumerNumberValidation } from "utils/getConsumerNumberValidation";
import moment from "moment";
import { getDocumentsValidationSchema } from "utils/getDocumentsValidationSchema";

import { useApplicantTypeQuery } from "apis/ApplicantType";

const useValidationSchema = () => {
  const formData = useSelector((state) => state?.RemovalOfConnection);
  const { data: { data: { items: applicantType = [] } = {} } = {} } =
    useApplicantTypeQuery();

  const validationSchema = useMemo(
    () => [
      Yup.object({
        utilityConsumerId: Yup.string().required("Customer number is required"),
        disconnectionDate: Yup.date().test(
          "isValidDisconnectionDate",
          "",
          (_, context) => {
            const [
              {
                value: {
                  disconnectionDate,
                  payableBillAmount,
                  // connectionLoad: { expectedConnectionEndDate } = {},
                } = {},
              } = {},
            ] = context.from || [];
            if (payableBillAmount <= 0) {
              if (!disconnectionDate) {
                return context.createError({
                  message: "Disconnection date is required",
                });
              }
              if (!disconnectionDate?.isValid()) {
                return context.createError({
                  message: "Enter a valid date",
                });
              }
              if (disconnectionDate.isSameOrBefore(moment())) {
                return context.createError({
                  message: "Disconnection date must be in future",
                });
              }
              // if (
              //   expectedConnectionEndDate &&
              //   disconnectionDate.isSameOrAfter(expectedConnectionEndDate)
              // ) {
              //   return context.createError({
              //     message:
              //       "Removal date can't be greater then expected end date",
              //   });
              // }
            }
            return true;
          }
        ),
        applicant: Yup.object({
          panNumber: Yup.string()
            .matches(
              /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/,
              "Please enter a valid Pan number"
            )
            .when("applicantTypeId", {
              is: applicantType?.find(
                (application) => application.name.toLowerCase() === "individual"
              )?.id,
              then: (schema) => schema,
              otherwise: (schema) => schema.required("Pan number is required"),
            }),
          isGSTRegistered: Yup.string().required("GST is required"),
          gstNumber: Yup.string()
            .test("isGstRequired", "", function (_, context) {
              const [{ value: { isGSTRegistered, gstNumber } } = {}] =
                context.from || [];
              if (isGSTRegistered && !gstNumber) {
                return context.createError({
                  message: "GST number is required",
                });
              }
              return true;
            })
            .matches(
              /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/,
              "Please enter a valid GST number"
            ),
        }),
      }),
      Yup.object({
        authorizedPerson: Yup.object({
          salutation: Yup.string().required("Salutation is required"),
          firstName: Yup.string()
            .matches(regexForName, "Please enter a valid first name")
            .required("First name is required"),
          middleName: Yup.string().matches(
            regexForName,
            "Please enter a valid middle name"
          ),
          lastName: Yup.string()
            .matches(regexForName, "Please enter a valid last name")
            .required("Last name is required"),
          // gender: Yup.string().required("Gender is required"),
          // ageCategory: Yup.string().required("Age category is required"),
          email: Yup.string()
            .email("Please enter valid email")
            .required("Email is required"),
          mobileNumber: Yup.string()
            .required("Mobile number is required")
            .matches(/^\d{10}$/, "Please enter valid mobile number"),
          landLine: Yup.string().matches(
            /^\d{8,11}$/,
            "Please enter a valid landline number"
          ),
          imageUrl: Yup.string().test(
            "imageRequired",
            "Please upload image",
            function () {
              const [{ value: current = null } = {}] = this.from || [];
              return !!(current?.image || current?.imageUrl);
            }
          ),
        }),
      }),

      Yup.object({
        documents: getDocumentsValidationSchema(),
      }),
      Yup.object({
        declaration: Yup.object({
          isConsumerDuesOutStanding: Yup.boolean(),
          consumerDuesOutstanding: Yup.string().when(
            "isConsumerDuesOutstanding",
            {
              is: true,
              then: (schema) => getConsumerNumberValidation(schema),
              otherwise: (schema) => schema,
            }
          ),
          isPremisesDuesOutstanding: Yup.boolean(),
          premisesDuesOutstanding: Yup.string().when(
            "isPremisesDuesOutstanding",
            {
              is: true,
              then: (schema) => getConsumerNumberValidation(schema),
              otherwise: (schema) => schema,
            }
          ),
          isFirmDuesOutstanding: Yup.boolean(),
          firmDuesOutstanding: Yup.string().when("isFirmDuesOutstanding", {
            is: true,
            then: (schema) => getConsumerNumberValidation(schema),
            otherwise: (schema) => schema,
          }),
        }),
      }),
    ],
    [formData.utilityAddress, formData.payableBillAmount]
  );

  const getValidationSchema = useCallback(
    (index) => validationSchema[index],
    [validationSchema]
  );
  return getValidationSchema;
};

export default useValidationSchema;
